/* ============================================================================
 * ЭКРАН ВХОДА В КОНСОЛЬ ИВО
 *
 * Аналог ui/console/auth.js, но на React и в теме консоли ИВО (светлая для
 * пилота / тёмная для ЦУП). Показывает:
 *   - состояние соединения с Keycloak (realm/issuer из /api/auth/me);
 *   - поля логина/пароля, кнопку входа с блокировкой на время запроса;
 *   - текст ошибки от бэкенда (Keycloak отдаёт неверный текст про «Invalid
 *     user credentials» — переводим на русский, чтобы оператор не гадал).
 * ========================================================================= */

import { useState } from 'react'
import type { FormEvent } from 'react'
import { KeyRound, Loader2, Radar, ShieldAlert } from 'lucide-react'

import type { AuthState } from '../../lib/ivoAuth'

/** Человеческий текст вместо сырых кодов Keycloak */
function humanError(raw: string): string {
  const s = raw.toLowerCase()
  if (s.includes('invalid_user_credentials') || s.includes('invalid user credentials')) {
    return 'Неверный логин или пароль'
  }
  if (s.includes('unauthorized_client')) return 'Клиент не разрешён для этой учётной записи'
  if (s.includes('user_disabled')) return 'Учётная запись отключена'
  if (s.includes('temporarily')) return 'Сервер авторизации временно недоступен, повторите'
  return raw
}

export function LoginScreen({ auth }: { auth: AuthState }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')

  const submit = async (ev: FormEvent) => {
    ev.preventDefault()
    if (!username || !password || auth.busy) return
    try {
      await auth.login(username.trim(), password)
    } catch {
      /* текст ошибки уже выставлен хуком в auth.error */
    }
  }

  return (
    <div className="flex h-full w-full items-center justify-center bg-canvas px-4 text-ink">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-xl border border-line bg-surface p-6 shadow-lg"
      >
        <div className="mb-5 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-soft text-accent">
            <Radar size={22} aria-hidden />
          </span>
          <div>
            <div className="text-sm font-bold tracking-wide">ЦУП · ИВО</div>
            <div className="text-[11px] text-ink-muted">
              Индикатор воздушной обстановки · УТМ России
            </div>
          </div>
        </div>

        <label className="mb-1 block text-[11px] font-semibold text-ink-muted" htmlFor="ivo-login-u">
          Логин
        </label>
        <input
          id="ivo-login-u"
          name="username"
          autoComplete="username"
          autoFocus
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="mb-3 w-full rounded-md border border-line bg-surface-alt px-3 py-2 text-sm outline-none focus:border-accent"
          placeholder="ivo-dispatcher"
        />

        <label className="mb-1 block text-[11px] font-semibold text-ink-muted" htmlFor="ivo-login-p">
          Пароль
        </label>
        <input
          id="ivo-login-p"
          name="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mb-4 w-full rounded-md border border-line bg-surface-alt px-3 py-2 text-sm outline-none focus:border-accent"
          placeholder="••••••••"
        />

        {auth.error && (
          <div
            role="alert"
            className="mb-4 flex items-start gap-2 rounded-md border border-critical-soft bg-critical-soft px-3 py-2 text-[12px] text-critical"
          >
            <ShieldAlert size={14} className="mt-0.5 shrink-0" aria-hidden />
            <span>{humanError(auth.error)}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={auth.busy || !username || !password}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-accent px-3 py-2 text-sm font-semibold text-ink-inverse disabled:cursor-not-allowed disabled:opacity-50"
        >
          {auth.busy ? <Loader2 size={15} className="animate-spin" aria-hidden /> : <KeyRound size={15} aria-hidden />}
          {auth.busy ? 'Проверяем…' : 'Войти'}
        </button>

        <div className="mt-4 text-[10px] leading-relaxed text-ink-muted">
          Доступ ролей: <b>Диспетчер</b> (ЦУП), <b>Пилот</b>, <b>Аудитор</b>.
          Сессия серверная, {auth.me?.expires_in ? `таймаут ${Math.round(auth.me.expires_in / 60)} мин` : 'таймаут 10 мин'};
          токен Keycloak в браузер не передаётся.
          <br />
          ГОСТ Р 71934-2025 · СК WGS-84 · высоты в метрах
        </div>
      </form>
    </div>
  )
}